import { Input } from "@/components/ui/input";
import { baseToIncl, inclToBase, lineCalc, r2, rateOf } from "@/lib/gstMath";

const showExcl = (v) => (typeof v === "number" && !Number.isInteger(v) ? String(Number(v.toFixed(6))) : v);

/** Unit price cell: entry mode is display-only — the saved unit_price_inr is always the exclusive price. */
export function GstPriceInput({ it, i, onPatch }) {
  const incl = it._price_mode === "INCL";
  const rate = rateOf(it);
  const value = incl ? (it._incl_draft ?? String(r2(baseToIncl(it.unit_price_inr, rate)))) : showExcl(it.unit_price_inr);
  const onChange = (e) => {
    const s = e.target.value;
    if (incl) onPatch({ _incl_draft: s, unit_price_inr: s === "" ? 0 : inclToBase(s, rate) });
    else onPatch({ unit_price_inr: s });
  };
  return (
    <div className="col-span-2 relative">
      <span className={`pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 rounded px-1 text-[8.5px] font-bold ${incl ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}
        data-testid={`new-quote-price-tag-${i}`}>{incl ? "INCL" : "EXCL"}</span>
      <Input className="h-8 pl-10 text-[12px] text-right" type="number" step="any" value={value} onChange={onChange}
        aria-label={incl ? "Unit price including GST" : "Unit price excluding GST"} data-testid={`new-quote-price-${i}`} />
    </div>
  );
}

const fmt = (v, cur) => `${cur && cur !== "INR" ? cur + " " : "₹"}${(Number(v) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Per-line entry selector + live breakdown (unit before discount; line = qty × unit − discount + GST). */
export function GstPriceBreakdown({ it, i, currency, onPatch }) {
  const incl = it._price_mode === "INCL";
  const c = lineCalc(it);
  const setMode = (m) => {
    if ((m === "INCL") === incl) return;
    onPatch(m === "INCL" ? { _price_mode: "INCL", _incl_draft: String(r2(c.unitIncl)) } : { _price_mode: "EXCL", _incl_draft: undefined });
  };
  const seg = (on) => `px-2 h-6 text-[10.5px] font-semibold ${on ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-slate-50 px-2 py-1.5 text-[10.5px] text-slate-600" data-testid={`new-quote-gst-breakdown-${i}`}>
      <span className="inline-flex overflow-hidden rounded border border-slate-300" role="group" aria-label="Unit price entered">
        <button type="button" className={seg(!incl)} onClick={() => setMode("EXCL")} data-testid={`new-quote-price-mode-excl-${i}`}>Excluding GST</button>
        <button type="button" className={seg(incl)} onClick={() => setMode("INCL")} data-testid={`new-quote-price-mode-incl-${i}`}>Including GST</button>
      </span>
      <span data-testid={`new-quote-price-scope-${i}`}>Price typed is <b>per unit, before discount</b>, {incl ? "including" : "excluding"} GST {c.rate}%</span>
      <span data-testid={`new-quote-unit-split-${i}`}>Unit: base <b className="font-mono">{fmt(c.unitBase, currency)}</b> + GST <b className="font-mono">{fmt(c.unitGst, currency)}</b> = <b className="font-mono">{fmt(c.unitIncl, currency)}</b></span>
      <span data-testid={`new-quote-line-split-${i}`}>Line ({c.qty} × unit{c.disc ? ` − ${c.disc}%` : ""}): base <b className="font-mono" data-testid={`new-quote-line-base-${i}`}>{fmt(c.sub, currency)}</b> + GST <b className="font-mono" data-testid={`new-quote-line-gst-${i}`}>{fmt(c.tax, currency)}</b> = <b className="font-mono text-slate-900" data-testid={`new-quote-line-incl-${i}`}>{fmt(c.total, currency)}</b></span>
    </div>
  );
}
